// SiteUP CRM (2026-09-18): mapa da página "Configurar" do Kanban.
// A configuração morava em quatro lugares (menu de três pontos, Gerenciar
// funis, raio da coluna, modais soltos). Aqui fica a única fonte de qual
// seção existe, quem pode ver, e para onde vão as URLs antigas.
// Lógica pura: nada de Vue, nada de store.

export const CONFIGURE_GROUPS = [
  { id: 'funnel', labelKey: 'KANBAN.CONFIGURE.GROUPS.FUNNEL' },
  { id: 'account', labelKey: 'KANBAN.CONFIGURE.GROUPS.ACCOUNT' },
];

/**
 * Cada item herda a permissão que ele já tinha no menu antigo:
 * `admin` = só administrador, `stacklab` = só instalação Stacklab,
 * sem nada = todo mundo que abre o Kanban.
 * A seção aparece quando pelo menos um item dela aparece.
 */
export const CONFIGURE_SECTIONS = [
  {
    id: 'stages',
    group: 'funnel',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.STAGES',
    icon: 'i-ph-kanban',
    items: [{ id: 'funnel_general', requires: 'admin' }],
  },
  {
    id: 'automations',
    group: 'funnel',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.AUTOMATIONS',
    icon: 'i-ph-lightning',
    items: [
      { id: 'stage_automations', requires: 'admin' },
      { id: 'quick_actions', requires: 'admin' },
      { id: 'message_templates', requires: 'stacklab' },
    ],
  },
  {
    id: 'agenda',
    group: 'funnel',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.AGENDA',
    icon: 'i-ph-calendar',
    items: [{ id: 'funnel_agenda', requires: 'admin' }],
  },
  {
    id: 'ai',
    group: 'funnel',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.AI',
    icon: 'i-ph-sparkle',
    items: [{ id: 'funnel_ai', requires: 'admin' }],
  },
  {
    id: 'access',
    group: 'funnel',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.ACCESS',
    icon: 'i-lucide-user',
    items: [{ id: 'funnel_access', requires: 'admin' }],
  },
  {
    id: 'appearance',
    group: 'account',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.APPEARANCE',
    icon: 'i-ph-eye',
    items: [
      { id: 'kanban_settings_preferences' },
      { id: 'card_templates', requires: 'admin' },
    ],
  },
  {
    id: 'offers',
    group: 'account',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.OFFERS',
    icon: 'i-ph-package',
    items: [
      { id: 'offers', requires: 'stacklab' },
      { id: 'offer_packages', requires: 'stacklab' },
    ],
  },
  {
    id: 'integrations',
    group: 'account',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.INTEGRATIONS',
    icon: 'i-ph-paper-plane-tilt',
    items: [
      // O webhook recebe os cards da conta inteira: só administrador.
      { id: 'kanban_settings_webhook', requires: 'admin' },
      { id: 'funnel_integrations', requires: 'admin' },
    ],
  },
  {
    id: 'data',
    group: 'account',
    labelKey: 'KANBAN.CONFIGURE.SECTIONS.DATA',
    icon: 'i-ph-arrows-down-up',
    items: [
      { id: 'export_items', requires: 'admin' },
      { id: 'import_items', requires: 'admin' },
    ],
  },
];

export const isItemAllowed = (
  item,
  { isAdmin = false, isStacklab = false }
) => {
  if (item?.requires === 'admin') return Boolean(isAdmin);
  if (item?.requires === 'stacklab') return Boolean(isStacklab);
  return true;
};

/** Itens permitidos de uma seção, na ordem declarada. */
export const allowedItemIds = (sectionId, permissions = {}) => {
  const section = CONFIGURE_SECTIONS.find(s => s.id === sectionId);
  if (!section) return [];
  return section.items
    .filter(item => isItemAllowed(item, permissions))
    .map(item => item.id);
};

/** Seções que a pessoa enxerga, já com a lista de itens permitidos. */
export const visibleConfigureSections = (permissions = {}) =>
  CONFIGURE_SECTIONS.map(section => ({
    ...section,
    items: section.items.filter(item => isItemAllowed(item, permissions)),
  })).filter(section => section.items.length > 0);

/** Sem nenhuma seção permitida a engrenagem nem aparece. */
export const canConfigureKanban = (permissions = {}) =>
  visibleConfigureSections(permissions).length > 0;

/**
 * Seção pedida na URL. Cai na primeira permitida quando o id não existe,
 * não é permitido ou veio vazio. Devolve null quando não há nada a mostrar.
 */
export const resolveConfigureSection = (sectionId, permissions = {}) => {
  const sections = visibleConfigureSections(permissions);
  if (!sections.length) return null;
  const found = sections.find(section => section.id === sectionId);
  return found ? found.id : sections[0].id;
};

/**
 * URLs antigas (`?view=funnels`, `?view=quick-actions`, ...) continuam
 * funcionando: cada uma aponta para a seção que absorveu aquela tela.
 * Aceita tanto o nome de view interno quanto o valor escrito na query.
 */
export const LEGACY_VIEW_TO_SECTION = {
  funnels: 'stages',
  'quick-actions': 'automations',
  quick_actions: 'automations',
  'stage-automations': 'automations',
  stage_automations: 'automations',
  templates: 'automations',
  offers: 'offers',
  'offer-packages': 'offers',
  offer_packages: 'offers',
};

export const legacyViewToSection = view => LEGACY_VIEW_TO_SECTION[view] || null;

/**
 * Query de redirecionamento de uma URL antiga. Devolve null quando a view
 * não é de configuração (kanban, list, agenda, map, scheduled_messages...),
 * e nesses casos nada muda.
 */
export const redirectLegacyConfigureQuery = (query = {}) => {
  const section = legacyViewToSection(query.view);
  if (!section) return null;
  const next = { ...query, view: 'configure', section };
  // `stage` sobrevive só para Automações, que sabe filtrar por etapa.
  if (section !== 'automations') delete next.stage;
  // `funnel`, `edit` e `offer` seguem intactos: são deep links para o funil
  // ou a oferta que o formulário embutido abre dentro da seção.
  return next;
};
